import { useEffect, useMemo, useState } from 'react';
import { useUi } from '@allrouted/ui/Toaster';
import { DatePicker } from '@allrouted/ui/DatePicker';
import { Drawer } from '@allrouted/ui/Drawer';
import { Field, NumberInput, TextArea, TextInput, TimeInput, Toggle } from '@allrouted/ui/Field';
import { Select } from '@allrouted/ui/Select';
import {
  api,
  ApiError,
  type FitLane,
  type FitOption,
  type FitRanking,
  type FitRequest,
  type FitResponse,
} from '../../lib/api';
import { addressLine, ampm, distance, hhmm } from '../../lib/format';
import { AddressAutocomplete, type PickedAddress } from './AddressAutocomplete';
import { routeColor } from '../../lib/route-color';
import type { BoardRoute } from '../../lib/types';

/**
 * "Could somebody fit this in today?"
 *
 * An address and a window go in; every driver's remaining day comes back with
 * the new stop tried in each gap. Nothing changes until the dispatcher picks
 * one and confirms -- only then is the order created (so the integration's
 * webhook fires as for any new order) and placed in that slot. The chosen
 * option is drawn on the board's map behind the drawer, which is why this one
 * has no scrim.
 *
 * Opened on an existing unassigned order instead, it searches with that
 * order's address, time on site and window, and confirming places that order
 * rather than creating another.
 */

interface OrderInfo {
  id: string;
  name: string;
  address: string;
  location: { lat: number; lng: number } | null;
  serviceTime: number;
  timeWindows: { start: number; end: number }[];
  date: string | null;
  requiredVehicleId: string | null;
  requiredVehicleName: string | null;
}

export interface FitPreview {
  point: { lat: number; lng: number };
  from: { lat: number; lng: number } | null;
  to: { lat: number; lng: number } | null;
  color: string;
  /** What the bubble on the pin says. */
  popup?: { title: string; lines: string[] };
}

const RANKINGS: { value: FitRanking; label: string }[] = [
  { value: 'driving', label: 'Least added driving' },
  { value: 'earliest', label: 'Earliest arrival' },
  { value: 'disruption', label: 'Least disruption to the day' },
  { value: 'lightest', label: 'Driver with the most room' },
];

const VERDICT: Record<FitOption['verdict'], { tone: 'ok' | 'warn' | 'late'; text: string }> = {
  fits: { tone: 'ok', text: 'Fits' },
  risky: { tone: 'warn', text: 'At a cost' },
  late: { tone: 'late', text: 'Misses window' },
};

interface Candidate {
  lane: FitLane;
  option: FitOption;
}

function laneName(lane: FitLane): string {
  return lane.technicianName ?? lane.vehicleName;
}

function minutes(seconds: number): string {
  const m = Math.round(seconds / 60);
  if (Math.abs(m) < 60) return `${m} min`;
  const h = Math.floor(Math.abs(m) / 60);
  return `${m < 0 ? '-' : ''}${h} h ${Math.abs(m) % 60} min`;
}

function stopName(ref: FitOption['afterStop']): string {
  if (!ref) return '';
  return ref.label ? `${ref.label} ${ref.name}` : ref.name;
}

/** Where in the day it goes, in the words a dispatcher would use on the phone. */
function placement(lane: FitLane, o: FitOption): string {
  if (o.position === 0) {
    if (lane.live && !lane.start?.stop)
      return o.beforeStop ? `Next, before ${stopName(o.beforeStop)}` : 'Next';
    if (o.afterStop)
      return o.beforeStop
        ? `After ${stopName(o.afterStop)}, before ${stopName(o.beforeStop)}`
        : `After ${stopName(o.afterStop)}`;
    return o.beforeStop ? `First, before ${stopName(o.beforeStop)}` : 'Only stop';
  }
  return o.beforeStop
    ? `After ${stopName(o.afterStop)}, before ${stopName(o.beforeStop)}`
    : `Last, after ${stopName(o.afterStop)}`;
}

function warnings(lane: FitLane, o: FitOption): string[] {
  const out: string[] = [];
  if (!o.inWindow) out.push('Arrives after its window closes');
  if (o.newlyLate.length > 0)
    out.push(`Makes ${o.newlyLate.length} later ${o.newlyLate.length === 1 ? 'stop' : 'stops'} late`);
  if (o.addedOvertimeSeconds > 0)
    out.push(`Runs ${minutes(o.addedOvertimeSeconds)} past the shift end (${ampm(lane.shiftEnd)})`);
  if (o.overStopLimit) out.push('Over this vehicle’s stop limit');
  return out;
}

export function FitInDrawer({
  open,
  territoryId,
  date,
  today,
  routes,
  order,
  onClose,
  onPreview,
  onPlaced,
}: {
  open: boolean;
  territoryId: string | null;
  /** The day the board is showing; the search starts there. */
  date: string;
  today: string;
  /** For each lane's colour, so the preview matches the line on the map. */
  routes: BoardRoute[];
  /** An unassigned order to find a spot for, instead of a new address. */
  order?: { id: string; name: string } | null;
  onClose: () => void;
  onPreview: (preview: FitPreview | null) => void;
  /** After an order has been placed, for a screen that does not hear the live feed. */
  onPlaced?: () => void;
}) {
  const ui = useUi();
  const [address, setAddress] = useState('');
  /* A suggestion picked from the list: its position, so no geocode is needed. */
  const [picked, setPicked] = useState<PickedAddress | null>(null);
  const [orderInfo, setOrderInfo] = useState<OrderInfo | null>(null);
  const [serviceMinutes, setServiceMinutes] = useState(45);
  const [windowStart, setWindowStart] = useState<number | null>(null);
  const [windowEnd, setWindowEnd] = useState<number | null>(null);
  const [day, setDay] = useState(date < today ? today : date);
  const [ranking, setRanking] = useState<FitRanking>('driving');
  const [showCostly, setShowCostly] = useState(false);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [result, setResult] = useState<FitResponse | null>(null);
  /* The request that produced `result`, with the address already resolved. */
  const [asked, setAsked] = useState<FitRequest | null>(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chosen, setChosen] = useState<Candidate | null>(null);

  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [reference, setReference] = useState('');
  const [type, setType] = useState('service');
  const [priority, setPriority] = useState(50);
  const [notes, setNotes] = useState('');
  /* Push the driver about their new stop. On by default; off for a quiet placement. */
  const [notify, setNotify] = useState(true);

  useEffect(() => {
    if (open && !order) setDay(date < today ? today : date);
  }, [open, date, today, order]);

  /*
   * An existing order: read it, then search straight away -- the dispatcher
   * asked a question by clicking, and a form to press again would be a step
   * with nothing to decide in it.
   */
  useEffect(() => {
    setOrderInfo(null);
    if (!open || !order || !territoryId) return;
    reset();
    let cancelled = false;
    api
      .order(territoryId, order.id)
      .then((o) => {
        if (cancelled) return;
        const lat = o.lat as number | null;
        const lng = o.lng as number | null;
        const info: OrderInfo = {
          id: order.id,
          name: String(o.name ?? order.name),
          address: addressLine(o.address as Parameters<typeof addressLine>[0]),
          location: lat !== null && lng !== null ? { lat: Number(lat), lng: Number(lng) } : null,
          serviceTime: Number(o.service_time ?? 0),
          timeWindows: Array.isArray(o.time_windows) ? (o.time_windows as OrderInfo['timeWindows']) : [],
          date: o.scheduled_date ? String(o.scheduled_date).slice(0, 10) : null,
          requiredVehicleId: (o.required_vehicle_id as string | null) ?? null,
          requiredVehicleName: (o.required_vehicle_name as string | null) ?? null,
        };
        setOrderInfo(info);
        if (!info.date) {
          setError('This order has no scheduled day. Give it one first, then find a fit.');
          return;
        }
        if (!info.location && !info.address) {
          setError('This order has no address or position to search from.');
          return;
        }
        void run(
          {
            ...(info.location ? { location: info.location } : { address: info.address }),
            serviceTime: info.serviceTime,
            timeWindows: info.timeWindows,
          },
          info.date,
        );
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Could not read that order.'));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, order?.id, territoryId]);

  const colorOf = useMemo(() => {
    const m = new Map<string, string>();
    routes.forEach((r, i) => m.set(r.vehicleId, routeColor(r, i)));
    return m;
  }, [routes]);
  const laneColor = (lane: FitLane) => colorOf.get(lane.vehicleId) ?? lane.vehicleColor ?? '#35b8c9';

  /* The map behind follows whichever option is picked. */
  useEffect(() => {
    if (!open || !result || !chosen) {
      onPreview(null);
      return;
    }
    const { lane, option } = chosen;
    const from = option.afterStop ?? (option.position === 0 ? (lane.start ?? null) : null);
    const to = option.beforeStop ?? lane.end ?? null;
    onPreview({
      point: result.location,
      from: from ? { lat: from.lat, lng: from.lng } : null,
      to: to ? { lat: to.lat, lng: to.lng } : null,
      color: laneColor(lane),
      popup: {
        title: orderInfo?.name ?? (address.split(',')[0] || 'New stop'),
        lines: [
          `${laneName(lane)} · ${placement(lane, option)}`,
          `Arrives ${ampm(option.arrival)} · +${minutes(Math.max(0, option.addedDriveSeconds))} driving`,
          ...warnings(lane, option).slice(0, 1),
        ],
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, result, chosen, colorOf]);

  const reset = () => {
    setResult(null);
    setAsked(null);
    setChosen(null);
    setConfirming(false);
    setError(null);
    setExpanded(new Set());
  };

  const close = () => {
    onPreview(null);
    reset();
    setOrderInfo(null);
    onClose();
  };

  const windowProblem =
    (windowStart === null) !== (windowEnd === null)
      ? 'Give both ends of the window, or neither.'
      : windowStart !== null && windowEnd !== null && windowEnd <= windowStart
        ? 'The window must end after it starts.'
        : null;

  const run = async (request: FitRequest, onDay: string): Promise<FitResponse | null> => {
    if (!territoryId) return null;
    setSearching(true);
    setError(null);
    try {
      const r = await api.fitIn(territoryId, onDay, request);
      setResult(r);
      /* Later runs reuse the position, so a confirm never re-geocodes. */
      setAsked({ ...request, location: r.location });
      return r;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not reach AllRouted.');
      return null;
    } finally {
      setSearching(false);
    }
  };

  const search = async () => {
    if (!address.trim() || windowProblem) return;
    if (day < today) {
      setError('Pick today or a later day.');
      return;
    }
    setChosen(null);
    setConfirming(false);
    setExpanded(new Set());
    await run(
      {
        ...(picked ? { location: picked.location } : { address: address.trim() }),
        serviceTime: Math.max(0, Math.round(serviceMinutes * 60)),
        timeWindows:
          windowStart !== null && windowEnd !== null ? [{ start: windowStart, end: windowEnd }] : [],
      },
      day,
    );
  };

  const candidates: Candidate[] = useMemo(() => {
    if (!result) return [];
    return (
      result.routes
        .flatMap((lane) => (lane.options ?? []).map((option) => ({ lane, option })))
        .filter((c) => showCostly || c.option.verdict === 'fits')
        /* An order reserved for one vehicle can only go on that one. */
        .filter((c) => !orderInfo?.requiredVehicleId || c.lane.vehicleId === orderInfo.requiredVehicleId)
        .sort((a, b) => a.option.rank[ranking] - b.option.rank[ranking])
    );
  }, [result, ranking, showCostly, orderInfo]);

  /* Best spot per driver first; the rest of a driver's spots open beneath it. */
  const byDriver = useMemo(() => {
    const groups = new Map<string, Candidate[]>();
    for (const c of candidates) {
      const list = groups.get(c.lane.vehicleId) ?? [];
      list.push(c);
      groups.set(c.lane.vehicleId, list);
    }
    return [...groups.values()];
  }, [candidates]);

  const hiddenCostly = result
    ? result.routes
        .filter((l) => !orderInfo?.requiredVehicleId || l.vehicleId === orderInfo.requiredVehicleId)
        .flatMap((l) => l.options ?? [])
        .filter((o) => o.verdict !== 'fits').length
    : 0;
  const skipped = result?.routes.filter((l) => l.skipped) ?? [];
  const nothingPlanned =
    result !== null && !result.live && result.routes.every((l) => (l.plannedStops ?? 0) === 0);

  /*
   * The best option is picked as soon as results arrive, so the map answers
   * the question the moment it is asked; clicking another card moves it.
   */
  useEffect(() => {
    if (result && !chosen && candidates[0]) setChosen(candidates[0]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, candidates]);

  const isChosen = (c: Candidate) =>
    chosen?.lane.vehicleId === c.lane.vehicleId && chosen.option.position === c.option.position;

  /*
   * Create the order and put it in the chosen slot.
   *
   * The preview is re-run first. A driver who finished a stop while this was
   * being read has moved the day on, and the slot number the preview gave may
   * now point in front of work already done. Same neighbours either side means
   * the same placement; anything else is shown again rather than guessed at.
   */
  const confirm = async () => {
    if (!territoryId || !chosen || !asked || !result) return;
    setSaving(true);
    try {
      const fresh = await run(asked, result.date);
      const lane = fresh?.routes.find((l) => l.vehicleId === chosen.lane.vehicleId);
      const same = lane?.options?.find(
        (o) =>
          (o.afterStop?.stopId ?? null) === (chosen.option.afterStop?.stopId ?? null) &&
          (o.beforeStop?.stopId ?? null) === (chosen.option.beforeStop?.stopId ?? null),
      );
      if (!fresh || !lane || !same) {
        setChosen(null);
        setConfirming(false);
        ui.notify(
          'info',
          'The day moved on',
          'That spot is no longer the same. The options have been refreshed; pick again.',
        );
        return;
      }

      if (orderInfo) {
        await api.assignOrder(
          territoryId,
          fresh.date,
          orderInfo.id,
          lane.routeId ?? `v:${lane.vehicleId}`,
          same.toSequence,
          notify,
        );
        ui.notify(
          'ok',
          `Added to ${laneName(lane)}’s day`,
          `${orderInfo.name} · ${placement(lane, same)} · arrives ${ampm(same.arrival)}`,
        );
        onPlaced?.();
        close();
        return;
      }

      const created = await api.createOrder(territoryId, {
        name: name.trim(),
        externalId: reference.trim() || null,
        type,
        /* The picked suggestion's parts where there is one, so the order reads like any other. */
        address: picked ? picked.address : { line1: address.trim() },
        location: fresh.location,
        serviceTime: asked.serviceTime,
        priority,
        timeWindows: asked.timeWindows,
        scheduledDate: fresh.date,
        notes: notes.trim() || null,
      });
      const orderId = String(created.orders[0]?.id ?? '');
      try {
        await api.assignOrder(
          territoryId,
          fresh.date,
          orderId,
          lane.routeId ?? `v:${lane.vehicleId}`,
          same.toSequence,
          notify,
        );
      } catch (err) {
        ui.notify(
          'error',
          'Order created, but not placed',
          `${err instanceof ApiError ? err.message : String(err)} It is in Unassigned; drag it onto a route.`,
        );
        close();
        return;
      }
      ui.notify(
        'ok',
        `Added to ${laneName(lane)}’s day`,
        `${name.trim()} · ${placement(lane, same)} · arrives ${ampm(same.arrival)}`,
      );
      setName('');
      setReference('');
      setNotes('');
      setAddress('');
      setPicked(null);
      onPlaced?.();
      close();
    } catch (err) {
      ui.notify(
        'error',
        orderInfo ? 'Could not place the order' : 'Could not create the order',
        err instanceof ApiError ? err.message : String(err),
      );
    } finally {
      setSaving(false);
    }
  };

  const unit = result?.distanceUnit ?? 'mi';

  const footer = confirming ? (
    <>
      <button type="button" className="btn" onClick={() => setConfirming(false)} disabled={saving}>
        Back
      </button>
      <button
        type="button"
        className="btn btn-primary"
        disabled={saving || !name.trim()}
        onClick={() => void confirm()}
      >
        {saving ? <span className="spinner" /> : null}
        Create and place
      </button>
    </>
  ) : (
    <>
      <button type="button" className="btn" onClick={close}>
        Close
      </button>
      {orderInfo ? (
        /* The order already exists: nothing more to ask, so place it directly. */
        <button
          type="button"
          className="btn btn-primary"
          disabled={!chosen || saving}
          onClick={() => void confirm()}
        >
          {saving ? <span className="spinner" /> : null}
          Place it here
        </button>
      ) : (
        <button
          type="button"
          className="btn btn-primary"
          disabled={!chosen}
          onClick={() => setConfirming(true)}
        >
          Use this spot
        </button>
      )}
    </>
  );

  return (
    <Drawer
      open={open}
      onClose={close}
      modeless
      title="Find a fit"
      subtitle={
        order
          ? 'Where this order would go. Nothing changes until you place it.'
          : 'Preview where one more stop would go. Nothing changes until you confirm.'
      }
      footer={footer}
    >
      {confirming && chosen ? (
        <>
          <div className="fit-summary">
            <span className="fit-dot" style={{ background: laneColor(chosen.lane) }} />
            <div>
              <b>{laneName(chosen.lane)}</b>
              <span>
                {placement(chosen.lane, chosen.option)} · arrives {ampm(chosen.option.arrival)}
              </span>
              <span>{address}</span>
            </div>
          </div>
          <Field label="Name">
            <TextInput value={name} onChange={setName} placeholder="WO 9001" />
          </Field>
          <Field label="Reference" hint="The ID your other systems already use">
            <TextInput value={reference} onChange={setReference} placeholder="WO-9001" />
          </Field>
          <Field label="Type">
            <Select
              value={type}
              options={[
                { value: 'service', label: 'Service' },
                { value: 'delivery', label: 'Delivery' },
                { value: 'pickup', label: 'Pickup' },
              ]}
              onChange={setType}
            />
          </Field>
          <Field label="Priority">
            <Select
              value={String(priority)}
              options={[
                { value: '25', label: 'Low' },
                { value: '50', label: 'Normal' },
                { value: '75', label: 'High' },
                { value: '100', label: 'Critical' },
              ]}
              onChange={(v) => setPriority(Number(v))}
            />
          </Field>
          <Field label="Notes" wide>
            <TextArea value={notes} rows={3} onChange={setNotes} />
          </Field>
          <Field label="Notify the driver" hint="A push notification to their phone">
            <Toggle checked={notify} onChange={setNotify} label={notify ? 'Send a notification' : 'Place quietly'} />
          </Field>
        </>
      ) : (
        <>
          {order ? (
            <div className="fit-order">
              <b>{orderInfo?.name ?? order.name}</b>
              {orderInfo ? (
                <>
                  <span>{orderInfo.address || 'No address; searching from its pin'}</span>
                  <span>
                    {Math.round(orderInfo.serviceTime / 60)} min on site ·{' '}
                    {orderInfo.timeWindows.length > 0
                      ? orderInfo.timeWindows.map((w) => `${hhmm(w.start)}–${hhmm(w.end)}`).join(', ')
                      : 'any time'}{' '}
                    · {orderInfo.date ?? 'no day'}
                  </span>
                  {orderInfo.requiredVehicleId ? (
                    <span>
                      Reserved for {orderInfo.requiredVehicleName ?? 'one vehicle'}: only that route is shown.
                    </span>
                  ) : null}
                </>
              ) : (
                <span>Reading the order…</span>
              )}
            </div>
          ) : (
            <>
              <Field label="Address" wide>
                <AddressAutocomplete
                  territoryId={territoryId}
                  value={address}
                  onChange={(v) => {
                    setAddress(v);
                    setPicked(null);
                    if (result) reset();
                  }}
                  onPick={setPicked}
                  placeholder="Start typing an address"
                />
              </Field>
              <Field label="Time on site">
                <NumberInput value={serviceMinutes} min={0} suffix="min" onChange={setServiceMinutes} />
              </Field>
              <Field label="Time window" hint="Leave empty for any time">
                <span className="fit-window">
                  <TimeInput seconds={windowStart} onChange={setWindowStart} />
                  <span>to</span>
                  <TimeInput seconds={windowEnd} onChange={setWindowEnd} />
                </span>
              </Field>
              <Field
                label="Day"
                hint={
                  day === today
                    ? 'Today, from where each driver is now'
                    : 'From the routes planned for that day'
                }
              >
                <DatePicker
                  value={day}
                  today={today}
                  onChange={(d) => {
                    setDay(d);
                    if (result) reset();
                  }}
                />
              </Field>
              {windowProblem ? <p className="fit-error">{windowProblem}</p> : null}
              <div className="fit-search">
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={searching || !address.trim() || Boolean(windowProblem) || !territoryId}
                  onClick={() => void search()}
                >
                  {searching ? <span className="spinner" /> : null}
                  {result ? 'Search again' : 'Find a fit'}
                </button>
              </div>
            </>
          )}
          {order && searching && !result ? <p className="fit-note">Trying every route…</p> : null}
          {error ? (
            <p className="fit-error" role="alert">
              {error}
            </p>
          ) : null}

          {result ? (
            <>
              <h3 className="section-label">Options</h3>
              {orderInfo ? (
                /* An existing order is placed straight from here, so its switch lives here too. */
                <div className="fit-notify">
                  <Toggle
                    checked={notify}
                    onChange={setNotify}
                    label={notify ? 'Notify the driver when placed' : 'Place quietly, no notification'}
                  />
                </div>
              ) : null}
              <div className="fit-controls">
                <Select value={ranking} options={RANKINGS} onChange={(v) => setRanking(v as FitRanking)} />
                <Toggle
                  checked={showCostly}
                  onChange={setShowCostly}
                  label={`Include spots that make others late or run over${hiddenCostly ? ` (${hiddenCostly})` : ''}`}
                />
              </div>

              {nothingPlanned ? (
                <p className="fit-note">
                  Nothing is planned on this day yet, so every driver would start from the depot. Build that
                  day’s routes first for a real answer.
                </p>
              ) : null}

              {byDriver.length === 0 ? (
                <p className="fit-note">
                  {hiddenCostly > 0
                    ? 'No driver can take it without making another stop late or running past their shift. Switch on the option above to see what it would cost.'
                    : 'No available driver can take it on this day.'}
                </p>
              ) : (
                <ol className="fit-list">
                  {byDriver.map((group) => {
                    const [best, ...rest] = group;
                    if (!best) return null;
                    const key = best.lane.vehicleId;
                    const open = expanded.has(key);
                    return (
                      <li key={key} className="fit-group">
                        {[best, ...(open ? rest : [])].map((c) => (
                          <OptionCard
                            key={c.option.position}
                            c={c}
                            unit={unit}
                            color={laneColor(c.lane)}
                            selected={isChosen(c)}
                            secondary={c !== best}
                            onPick={() => setChosen(c)}
                          />
                        ))}
                        {rest.length > 0 ? (
                          <button
                            type="button"
                            className="fit-more"
                            onClick={() =>
                              setExpanded((s) => {
                                const next = new Set(s);
                                if (open) next.delete(key);
                                else next.add(key);
                                return next;
                              })
                            }
                          >
                            {open
                              ? 'Fewer spots'
                              : `${rest.length} more ${rest.length === 1 ? 'spot' : 'spots'} on this route`}
                          </button>
                        ) : null}
                      </li>
                    );
                  })}
                </ol>
              )}

              {result.routes.some((l) => l.assumed) ? (
                <p className="fit-note">
                  {result.routes
                    .filter((l) => l.assumed)
                    .map(laneName)
                    .join(', ')}{' '}
                  {result.routes.filter((l) => l.assumed).length === 1 ? 'has' : 'have'} not reported progress
                  today, so their day is assumed to be running to plan.
                </p>
              ) : null}
              {skipped.length > 0 ? (
                <p className="fit-note">
                  Not considered:{' '}
                  {skipped.map((l) => `${laneName(l)} (${(l.skipped ?? '').toLowerCase()})`).join(', ')}.
                </p>
              ) : null}
              <p className="fit-note">
                {result.live && result.now !== null ? `As of ${ampm(result.now)}. ` : ''}
                Only the new stop is slotted in; no one else’s order changes.
              </p>
            </>
          ) : null}
        </>
      )}
    </Drawer>
  );
}

function OptionCard({
  c,
  unit,
  color,
  selected,
  secondary,
  onPick,
}: {
  c: Candidate;
  unit: 'mi' | 'km';
  color: string;
  selected: boolean;
  secondary: boolean;
  onPick: () => void;
}) {
  const { lane, option: o } = c;
  const verdict = VERDICT[o.verdict];
  const notes = warnings(lane, o);
  return (
    <button
      type="button"
      className="fit-card"
      data-selected={selected ? 'true' : undefined}
      data-secondary={secondary ? 'true' : undefined}
      onClick={onPick}
    >
      <span className="fit-card-head">
        <span className="fit-dot" style={{ background: color }} />
        <b>{laneName(lane)}</b>
        <span className="fit-verdict" data-tone={verdict.tone}>
          {verdict.text}
        </span>
      </span>
      <span className="fit-card-where">{placement(lane, o)}</span>
      <span className="fit-card-facts">
        <span>
          Arrives <b>{ampm(o.arrival)}</b>
          {o.waitSeconds > 60 ? ` (waits ${minutes(o.waitSeconds)} for the window)` : ''}
        </span>
        <span>
          +{minutes(Math.max(0, o.addedDriveSeconds))} driving · +
          {distance(Math.max(0, o.addedDistanceMeters), unit)}
        </span>
        <span>
          Day ends {ampm(o.routeEnd)} ({o.addedWorkSeconds >= 0 ? '+' : ''}
          {minutes(o.addedWorkSeconds)})
        </span>
        {o.maxPushSeconds > 60 ? <span>Later stops move up to {minutes(o.maxPushSeconds)}</span> : null}
      </span>
      {notes.length > 0 ? (
        <span className="fit-card-warn">
          {notes.map((n) => (
            <span key={n}>{n}</span>
          ))}
        </span>
      ) : null}
    </button>
  );
}
